import React, { useEffect, useMemo, useState, useRef } from 'react';
import FaultyTerminal from '../components/FaultyTerminal';
import { trackEvent } from '../firebaseAnalytics';
import LaunchCta from '../components/LaunchCta';
import { useTheme } from '../theme/ThemeProvider';
 
const MemoizedFaultyTerminal = React.memo(FaultyTerminal);

export default function Hero(){
  const { theme } = useTheme();
  const { allowAnimation, isMobile } = useHeroRendering();
  const pageVisible = usePageVisibility();
  const [heroRef, heroVisible] = useVisibilityObserver(0.2);
  // The terminal is part of the hero in both themes. Its tint and brightness
  // below keep the light version legible without changing the light surface.
  const shouldAnimate = allowAnimation && pageVisible && heroVisible && !isMobile;

  const terminalProps = useMemo(()=>{
    // The shader is a textured background, so retina-level canvas resolution
    // adds a lot of fragment work without making the terminal look sharper.
    const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1440;
    const devicePixelRatio = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
    const renderDpr = viewportWidth >= 2560 ? 0.75 : Math.min(devicePixelRatio, 1);
    return {
      scale: 3,
      gridMul: [2,1],
      digitSize: 3,
      timeScale: 1.6,
      scanlineIntensity: 0.95,
      glitchAmount: 1.8,
      flickerAmount: 0.8,
      noiseAmp: 0.55,
      chromaticAberration: 0,
      dither: 0,
      curvature: 0.06,
      tint: theme === 'light' ? '#6D46D9' : '#7C4DFF',
      mouseReact: false,
      mouseStrength: 0.35,
      pageLoadAnimation: false,
      brightness: theme === 'light' ? 0.32 : 0.42,
      transparentBackground: theme === 'light',
      dpr: renderDpr,
      maxFps: 30,
      pause: !shouldAnimate
    };
  }, [shouldAnimate, theme]);

  return (
    <>
      <div className="stars" aria-hidden="true"></div>
      <div className="scroll-blur" aria-hidden="true"></div>

      <section id="top" className="hero" data-bg="purple" style={{paddingTop:110}} ref={heroRef}>
        {/* Faulty Terminal background (Reactbits) */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            bottom: '-22vh',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '100vw',
            zIndex: 0,
            pointerEvents: 'none'
          }}
        >
          {shouldAnimate ? (
            <MemoizedFaultyTerminal {...terminalProps} />
          ) : (
            <div className="terminal-fallback" aria-hidden="true"></div>
          )}
        </div>

        {/* Fallback soft fade to blend in browsers without backdrop-filter (kept minimal; page blur does most of the work) */}

        <div className="container hero-inner">
          <h1 className="title-xl">Promptera</h1>
          <p className="subtitle">Organize, test, and orchestrate your prompts, agents, and workflows</p>
          <LaunchCta
            className="btn btn-primary"
            source="hero"
            withArrow
          />
          <div className="icon-row" aria-label="Social links">
            {/* <a className="icon-btn" href="https://instagram.com" target="_blank" rel="noopener" aria-label="Instagram"><i className="fi fi-brands-instagram"></i></a>
            <a className="icon-btn" href="https://x.com" target="_blank" rel="noopener" aria-label="X"><i className="fi fi-brands-twitter-alt"></i></a>
            <a className="icon-btn" href="https://linkedin.com" target="_blank" rel="noopener" aria-label="LinkedIn"><i className="fi fi-brands-linkedin"></i></a> */}
          </div>

          {/* Scroll down arrow to next section */}
          <a className="down-arrow" href="#quote" aria-label="Scroll to next section">
            <i className="fi fi-rr-angle-small-down"></i>
          </a>
        </div>
      </section>
    </>
  );
}

function useHeroRendering(){
  const [state, setState] = useState({ allowAnimation: false, isMobile: false });

  useEffect(()=>{
    if(typeof window === 'undefined'){ return; }
    const breakpointQuery = window.matchMedia('(max-width: 900px)');
    const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const webglSupported = supportsWebGL();

    const update = ()=>{
      setState({
        isMobile: breakpointQuery.matches,
        allowAnimation: !reducedMotionQuery.matches && webglSupported
      });
    };

    update();

    const detachBreakpoint = addMediaListener(breakpointQuery, update);
    const detachReducedMotion = addMediaListener(reducedMotionQuery, update);

    return ()=>{
      detachBreakpoint();
      detachReducedMotion();
    };
  }, []);

  return state;
}

function addMediaListener(query, handler){
  if(!query) return ()=>{};
  if(typeof query.addEventListener === 'function'){
    query.addEventListener('change', handler);
    return ()=> query.removeEventListener('change', handler);
  }
  if(typeof query.addListener === 'function'){
    query.addListener(handler);
    return ()=> query.removeListener(handler);
  }
  return ()=>{};
}

function supportsWebGL(){
  if(typeof window === 'undefined' || !window.WebGLRenderingContext){ return false; }
  try{
    const canvas = document.createElement('canvas');
    return !!(canvas.getContext('webgl') || canvas.getContext('experimental-webgl'));
  }catch(e){
    return false;
  }
}

function useVisibilityObserver(threshold = 0.15){
  const ref = useRef(null);
  const [visible, setVisible] = useState(true);

  useEffect(()=>{
    const node = ref.current;
    if(!node || typeof IntersectionObserver === 'undefined'){ return; }

    const observer = new IntersectionObserver(
      (entries)=>{
        entries.forEach(entry=>{
          if(entry.target === node){
            setVisible(entry.isIntersecting);
          }
        });
      },
      { threshold }
    );
    observer.observe(node);
    return ()=> observer.disconnect();
  }, [threshold]);

  return [ref, visible];
}

function usePageVisibility(){
  const [visible, setVisible] = useState(
    typeof document === 'undefined' ? true : !document.hidden
  );

  useEffect(()=>{
    if(typeof document === 'undefined') return;
    const handler = ()=> setVisible(!document.hidden);
    document.addEventListener('visibilitychange', handler);
    return ()=> document.removeEventListener('visibilitychange', handler);
  }, []);

  return visible;
}
